<template>
  <Form>
    <template #header>
      {{ $t("projects.form.thirdStep.label") }}
    </template>

    <div class="is-label">
      {{ $t("projects.form.thirdStep.projectImageLabel") }}
    </div>
    <div class="is-indent">
      <AuUpload
        :src="src"
        :button="$t('projects.form.thirdStep.projectChooseImageButton')"
        :required="required"
        width="100%"
        @change="(data) => ({ src, image } = data)"
      />
    </div>
    <div class="is-label">
      {{ $t("projects.form.thirdStep.projectIconLabel") }}
    </div>
    <div class="is-indent">
      <AuUpload
        :src="iconSrc"
        :button="$t('projects.form.thirdStep.projectChooseImageButton')"
        width="100%"
        @change="
          (data) => {
            iconSrc = data.src;
            iconImage = data.image;
          }
        "
      />
    </div>
    <div class="is-label">
      {{ $t("projects.form.thirdStep.projectDocsLabel") }}
    </div>
    <div class="is-indent">
      <AuUpload
        :files="fileList"
        :button="$t('projects.form.thirdStep.projectAddDocsButton')"
        mode="multiple"
        width="100%"
        embedded
        @added="(f) => fileList.push(f)"
        @deleted="(index) => fileList.splice(index, 1)"
        @embeddedChange="embeddedChange"
      />
    </div>
    <div
      v-for="(capitalRisk, i) in capitalRisks"
      v-show="isRwa"
      :key="i"
      class="is-indent risk-panel"
    >
      <div class="is-indent flex items-end">
        <AuInput
          v-model="capitalRisk.caption"
          label="Caption risk"
          placeholder="Input caption risk"
        />
        <AuButton
          bordered
          center
          :disabled="capitalRisks.length === 1"
          @click="deleteRisk(i)"
        >
          -
        </AuButton>
        <AuButton
          type="primary"
          @click="addRisk"
        >
          +
        </AuButton>
      </div>
      <AuInput
        v-model="capitalRisk.description"
        label="Description risk"
        type="TextArea"
      />
    </div>
    <ProgressLoader
      v-if="loading"
      mode="indeterminate"
      color="#5AC396"
      size="150px"
    />
    <div
      v-if="errorMsg !== null"
      class="error"
    >
      {{ errorMsg }}
    </div>

    <template
      v-if="!loading"
      #footer
    >
      <AuButton
        width="125px"
        bordered
        center
        @click="prevStep"
      >
        {{ $t("projects.form.backButton") }}
      </AuButton>

      <AuButton
        bordered
        center
        type="primary"
        @click="send"
      >
        <span class="bnt-text">
          {{ $t("projects.form.sendButton") }}
        </span>
      </AuButton>
    </template>
  </Form>
</template>

<script>
import _ from "lodash";
import AuButton from "@/components/extended/AuButton.vue";
import ProgressLoader from "@/loaders/progress-bar";
import { checkAccess, scope } from "@/help/accessData";
import { mapActions } from "vuex";
export default {
  name: "ProjectFormThirdStep",
  components: {
    AuButton,
    ProgressLoader
  },
  props: {
    id: {
      type: String,
      default: "new"
    },
    loading: {
      type: Boolean,
      default: false
    },
    error: {
      type: String,
      default: null
    },
    thirdPartOfProjectInfo: {
      type: Object,
      default: () => {}
    }
  },

  data() {
    return {
      src: null,
      image: null,
      oldImage: null,
      required: false,
      iconSrc: null,
      iconImage: null,
      oldIconImage: null,
      errorText: null,
      fileList: [],
      capitalRisks: [{
        caption: "",
        description: ""
      }],
      maxSize: 2 * 1024 * 1024
    };
  },

  computed: {
    isRwa() {
      return !checkAccess(scope.notRwa);
    },

    errorMsg() {
      if (this.errorText && this.errorText !== "") {
        return this.errorText;
      }
      if (this.error && this.error !== "") {
        return this.error;
      }
      return null;
    }
  },

  async mounted() {
    if (this.thirdPartOfProjectInfo !== null) {
      this.src = this.thirdPartOfProjectInfo.src;
      this.image = this.thirdPartOfProjectInfo.image;
      this.iconSrc = this.thirdPartOfProjectInfo.iconSrc;
      this.iconImage = this.thirdPartOfProjectInfo.iconImage;
      this.fileList = this.thirdPartOfProjectInfo.fileList || [];
      if (this.isRwa) {
        this.capitalRisks = this.thirdPartOfProjectInfo.capitalRisks || [{ caption: "", description: "" }];
      }
    }
    if (this.id !== "new" && !this.thirdPartOfProjectInfo) {
      const requestToIssue = await this.getRequestToIssue(this.id);
      this.setFields(requestToIssue);
    }
  },

  methods: {
    ...mapActions({
      getUploadedFile: "files/getUploadedFile",
      getRequestToIssue: "getRequestToIssue"
    }),

    prevStep() {
      const params = {
        src: this.src,
        image: this.image,
        oldImage: this.oldImage,
        iconSrc: this.iconSrc,
        iconImage: this.iconImage,
        oldIconImage: this.oldIconImage,
        fileList: this.fileList
      };
      if (this.isRwa) {
        params.capitalRisks = this.capitalRisks;
      }
      this.$emit("thirdPartOfProjectInfo", params);
      this.$emit("prevStep");
    },

    addRisk() {
      this.capitalRisks.push({ caption: "", description: "" });
    },

    deleteRisk(index) {
      this.capitalRisks.splice(index, 1);
    },

    goBack() {
      this.$router.push({ name: "my projects" });
    },

    embeddedChange(index, embedded) {
      this.fileList[index].embeddedCode = embedded;
      if (embedded) {
        this.fileList[index].isVideoEmbedded = true;
      }
      else {
        this.fileList[index].isVideoEmbedded = false;
      }
    },

    isImageGood(image) {
      const mbSize = this.maxSize / (1024 * 1024);
      if (image.size > this.maxSize) {
        this.errorText = `${this.$t(
          "projects.form.thirdStep.errorBigImage"
        )} ${mbSize} Mb`;
        return false;
      }
      const fileType = image.type ?? "";
      if (fileType.slice(0, fileType.indexOf("/")) !== "image") {
        this.errorText = this.$t("projects.form.thirdStep.errorNotImage");
        return false;
      }
      return true;
    },

    isFilesGood() {
      const mbSize = this.maxSize / (1024 * 1024);
      for (const file of this.fileList) {
        if (file.size > this.maxSize) {
          this.errorText =
            file.name +
            ` ${this.$t("projects.form.thirdStep.errorBigFile")} ${mbSize} Mb`;
          return false;
        }
      }
      return true;
    },

    send() {
      if (!this.image && !this.oldImage) {
        this.errorText = this.$t("projects.form.thirdStep.errorAddImage");
        this.required = true;
        return;
      }
      if (this.image !== null && !this.isImageGood(this.image)) {
        return;
      }
      if (this.iconImage !== null && !this.isImageGood(this.iconImage)) {
        return;
      }
      if (this.fileList.length > 0 && !this.isFilesGood()) {
        return;
      }
      const params = {
        src: this.src,
        image: this.image,
        oldImage: this.oldImage,
        iconSrc: this.iconSrc,
        iconImage: this.iconImage,
        oldIconImage: this.oldIconImage,
        fileList: this.fileList
      };
      if (this.isRwa) {
        params.capitalRisks = _.map(this.capitalRisks, el => JSON.stringify(el));
      }
      this.$emit("thirdPartOfProjectInfo", params);
      this.$emit("send");
    },

    async setFields(data) {
      this.image = null;
      this.fileList = [];

      if (!_.isNil(data?.mainImage)) {
        const id = data.mainImage.slice(data.mainImage.lastIndexOf("/") + 1);
        const file = await this.getUploadedFile(id);
        this.oldImage = file;
        this.src = file?.name;
      }
      if (!_.isNil(data?.icon)) {
        const id = data.icon.slice(data.icon.lastIndexOf("/") + 1);
        const file = await this.getUploadedFile(id);
        this.oldIconImage = file;
        this.iconSrc = file?.name;
      }
      if (this.isRwa) {
        if (data?.risks) {
          this.capitalRisks = _.map(data?.risks, el => JSON.parse(el));
        }
        else {
          this.capitalRisks = [{ caption: "", description: "" }];
        }
      }
      if (data?.additionalFile?.length > 0) {
        for (let index = 0; index < data.additionalFile.length; index++) {
          const id = data.additionalFile[index].slice(
            data.additionalFile[index].lastIndexOf("/") + 1
          );
          const file = await this.getUploadedFile(id);
          if (file) {
            file.name = file.title;
            this.fileList.push(file);
          }
        }
      }
    }
  }
};
</script>

<style scoped lang="scss">
.is3 {
  font-weight: 400;
  font-size: 14px;
  padding: 20px;
}
.is-back {
  border: 1px dashed var(--color-80);
  border-radius: 15px;
  width: 180px;
  height: 44px;
  margin-right: 15px;
  &:hover {
    cursor: pointer;
  }
}
.is-send {
  border: 1px solid var(--color-80);
  border-radius: 12px;
  background: #289566;
  height: 44px;
  width: 100%;
  &:hover {
    cursor: pointer;
  }
}
.is-label {
  margin-bottom: 15px;
}
.is-indent {
  margin-bottom: 35px;
  gap: 10px;

  &:last-child {
    margin-bottom: 0px;
  }
}
.error {
  display: flex;
  justify-content: center;
  margin-bottom: 10px;
  color: red;
}
.risk-panel {
  border-radius: 12px;
  border: 2px solid var(--border-color);
  padding: 20px;
}
</style>